import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EXPERIENCE_LEVELS, BRAND } from "@/lib/constants";
import {
  X,
  Send,
  Loader2,
  CheckCircle2,
  MessageCircle,
  Zap,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DRAFT_KEY = "iw_easy_apply_draft";

/**
 * LinkedIn-style 1-tap Easy Apply modal.
 * Minimal fields, pre-filled role, drafts persisted to localStorage so the
 * user can re-open without losing data.
 */
export default function EasyApplyModal({ open, onClose, role }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    experience: "",
    portfolio_url: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // Load saved basics from localStorage — so repeat applications are instant
  useEffect(() => {
    if (!open) return;
    setDone(false);
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        setForm((f) => ({ ...f, ...saved }));
      }
    } catch (_) { /* ignore */ }
  }, [open]);

  // Lock page scroll while modal open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [open]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.experience) {
      toast.error("Please fill name, phone, email and experience.");
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/careers`, {
        ...form,
        role: role || "General",
        cover_note: "",
      });
      // Persist basics for future easy apply
      try {
        localStorage.setItem(
          DRAFT_KEY,
          JSON.stringify({
            name: form.name,
            phone: form.phone,
            email: form.email,
            experience: form.experience,
            portfolio_url: form.portfolio_url,
          })
        );
      } catch (_) { /* ignore */ }
      if (typeof window !== "undefined") {
        if (typeof window.fbq === "function") {
          window.fbq("trackCustom", "JobApplication", {
            role,
            experience: form.experience,
            source: "easy_apply",
          });
        }
        if (typeof window.gtag === "function") {
          window.gtag("event", "job_application", {
            event_category: "Careers",
            event_label: role,
            method: "easy_apply",
          });
        }
      }
      setDone(true);
      toast.success("Application sent — we'll reach out soon.");
    } catch (err) {
      console.error(err);
      toast.error("Could not submit. Please WhatsApp us directly.");
    } finally {
      setLoading(false);
    }
  };

  const waLink = () => {
    const lines = [
      `Namaste ${BRAND.name} team,`,
      ``,
      `I just applied for *${role}* via Easy Apply.`,
      `• Name: ${form.name}`,
      `• Phone: ${form.phone}`,
      `• Email: ${form.email}`,
      `• Experience: ${form.experience}`,
    ];
    if (form.portfolio_url) lines.push(`• Portfolio: ${form.portfolio_url}`);
    return `https://wa.me/${BRAND.hrWhatsapp}?text=${encodeURIComponent(lines.join("\n"))}`;
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop + Flex center wrapper */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] grid place-items-center p-4 overflow-y-auto"
            style={{ background: "rgba(5,5,5,0.85)", backdropFilter: "blur(8px)" }}
            onClick={onClose}
            data-testid="easy-apply-backdrop"
          >
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.97 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full max-w-[540px] my-auto"
              style={{
                background:
                  "linear-gradient(180deg, rgba(26,20,20,0.98) 0%, rgba(20,16,16,0.98) 100%)",
                border: "1px solid rgba(212,175,55,0.35)",
                boxShadow:
                  "0 40px 100px -10px rgba(232,165,180,0.3), 0 0 0 1px rgba(212,175,55,0.15) inset",
              }}
              onClick={(e) => e.stopPropagation()}
              data-testid="easy-apply-modal"
            >
            {/* Close */}
            <button
              onClick={onClose}
              aria-label="Close"
              className="absolute top-4 right-4 w-8 h-8 grid place-items-center text-[var(--iw-faint)] hover:text-[var(--iw-gold)] border border-white/10 hover:border-[var(--iw-gold)]/50 transition-colors z-10"
              data-testid="easy-apply-close"
            >
              <X size={14} />
            </button>

            <div className="p-6 md:p-8">
              {done ? (
                <div className="text-center py-4">
                  <CheckCircle2
                    className="text-[var(--iw-gold)] mx-auto mb-5"
                    size={56}
                    strokeWidth={1}
                  />
                  <h3
                    className="font-serif-display text-[var(--iw-ivory)] text-3xl md:text-4xl leading-tight"
                    style={{ fontFamily: "Cormorant Garamond, serif" }}
                  >
                    Application{" "}
                    <span className="italic gold-rose-gradient">received</span>.
                  </h3>
                  <p className="text-[var(--iw-muted)] mt-3 leading-relaxed text-sm">
                    We read every note personally. If your craft resonates,
                    expect a WhatsApp within 72 hours.
                  </p>
                  <a
                    href={waLink()}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-gold mt-6 w-full justify-center"
                    data-testid="easy-apply-wa"
                  >
                    <MessageCircle size={13} /> Share on WhatsApp too
                  </a>
                  <button
                    onClick={onClose}
                    className="btn-ghost-gold mt-3 w-full justify-center"
                    data-testid="easy-apply-done-close"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <form onSubmit={submit}>
                  {/* Header */}
                  <div className="flex items-center gap-2 mb-2">
                    <Zap size={13} className="text-[var(--iw-gold)]" fill="currentColor" />
                    <span className="overline text-[var(--iw-gold)]">
                      Easy Apply · 30 seconds
                    </span>
                  </div>
                  <h3
                    className="font-serif-display text-[var(--iw-ivory)] text-3xl md:text-4xl leading-[1.05] tracking-tight"
                    style={{ fontFamily: "Cormorant Garamond, serif" }}
                    data-testid="easy-apply-title"
                  >
                    Apply for{" "}
                    <span className="italic gold-rose-gradient">
                      {role || "a role"}
                    </span>
                  </h3>
                  <p className="text-[var(--iw-muted)] text-sm mt-2">
                    Just the essentials. A real person replies on WhatsApp
                    within 72 hours.
                  </p>

                  <div className="mt-6 grid grid-cols-1 gap-4">
                    <LabeledInput
                      label="Full Name"
                      type="text"
                      value={form.name}
                      onChange={(v) => update("name", v)}
                      placeholder="Your name"
                      testid="easy-apply-name"
                      required
                      autoFocus
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <LabeledInput
                        label="Phone"
                        type="tel"
                        value={form.phone}
                        onChange={(v) => update("phone", v)}
                        placeholder="+91"
                        testid="easy-apply-phone"
                        required
                      />
                      <LabeledInput
                        label="Email"
                        type="email"
                        value={form.email}
                        onChange={(v) => update("email", v)}
                        placeholder="you@email.com"
                        testid="easy-apply-email"
                        required
                      />
                    </div>

                    <label className="flex flex-col gap-2">
                      <span className="overline text-[var(--iw-faint)]">
                        Experience <span className="text-[var(--iw-gold)]">·</span>
                      </span>
                      <Select
                        value={form.experience}
                        onValueChange={(v) => update("experience", v)}
                      >
                        <SelectTrigger
                          className="input-luxe h-[50px] rounded-none"
                          data-testid="easy-apply-exp-trigger"
                        >
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent
                          className="border-white/15 rounded-none"
                          style={{
                            background: "#0a0a0a",
                            color: "var(--iw-ivory)",
                          }}
                        >
                          {EXPERIENCE_LEVELS.map((x) => (
                            <SelectItem
                              key={x}
                              value={x}
                              className="font-sans-body"
                            >
                              {x}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </label>

                    <LabeledInput
                      label="Portfolio / Behance / Drive (optional)"
                      type="url"
                      value={form.portfolio_url}
                      onChange={(v) => update("portfolio_url", v)}
                      placeholder="https://..."
                      testid="easy-apply-portfolio"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-gold mt-7 w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="easy-apply-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        Send Application <Send size={13} />
                      </>
                    )}
                  </button>

                  <p className="overline text-[var(--iw-faint)] mt-4 text-center text-[10px]">
                    No auto-rejections · Founder reads every note
                  </p>
                </form>
              )}
            </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

const LabeledInput = ({
  label,
  type,
  value,
  onChange,
  placeholder,
  required,
  autoFocus,
  testid,
}) => (
  <label className="flex flex-col gap-2">
    <span className="overline text-[var(--iw-faint)]">
      {label} {required && <span className="text-[var(--iw-gold)]">·</span>}
    </span>
    <input
      type={type}
      className="input-luxe"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      required={required}
      autoFocus={autoFocus}
      data-testid={testid}
    />
  </label>
);
